'use client';

import React from 'react';
import Link from 'next/link';
import { Project } from '@/domains/projects/schemas';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { GripVertical, Copy, Trash2, X, ArrowRightLeft } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface CompactProjectCardProps {
  project: Partial<Project & { isSeries?: boolean }>;
  onDelete: (project: Partial<Project>) => void;
  onDuplicate: (project: Partial<Project>) => void;
  onTransfer?: (project: Partial<Project>) => void;
  onRemoveFromGroup: (projectId: string) => void;
}

export function CompactProjectCard({
  project,
  onDelete,
  onDuplicate,
  onTransfer,
  onRemoveFromGroup,
}: CompactProjectCardProps) {
  const isSeries = !!(project as any).isSeries;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: project.id! });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleAction = (e: React.MouseEvent, action: () => void) => {
    e.preventDefault();
    e.stopPropagation();
    action();
  };

  return (
    <div ref={setNodeRef} style={style} className={cn(isDragging && 'opacity-50')}>
      <Card className="hover:border-primary transition-colors duration-300 group/card flex flex-col h-full">
        <div className="flex items-start gap-2 p-3">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground mt-0.5 shrink-0"
          >
            <GripVertical className="h-4 w-4" />
          </div>

          <Link href={`/projects/${project.id!}`} className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h3 className="font-medium text-sm leading-tight truncate group-hover/card:text-primary transition-colors duration-300">
                  {project.title}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {project.updatedAt
                    ? formatDistanceToNow(new Date(project.updatedAt), { addSuffix: true })
                    : 'recently'}
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground line-clamp-2 mt-1.5">
              {project.blurb}
            </p>
          </Link>

          <div className="flex items-center shrink-0 opacity-0 group-hover/card:opacity-100 transition-opacity">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={(e) => handleAction(e, () => onDuplicate(project))}
              aria-label={`Duplicate ${project.title}`}
            >
              <Copy className="h-3 w-3" />
            </Button>
            {onTransfer && (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => handleAction(e, () => onTransfer(project))}
                        disabled={isSeries}
                        aria-label={`Transfer ${project.title}`}
                      >
                        <ArrowRightLeft className="h-3 w-3" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {isSeries && (
                    <TooltipContent>
                      <p>Unlink this project from its series before transferring</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={(e) => handleAction(e, () => onRemoveFromGroup(project.id!))}
              aria-label={`Remove ${project.title} from group`}
            >
              <X className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-destructive hover:text-destructive"
              onClick={(e) => handleAction(e, () => onDelete(project))}
              aria-label={`Delete ${project.title}`}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
